/**
 * AddressAutocomplete for Calendar meetings
 * Uses google-places-proxy edge function for smart address search
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MapPin, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { placesAutocomplete, placesDetails } from '@/lib/google-places';

interface Prediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  language?: 'en' | 'fr';
}

export function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  language = 'en',
}: AddressAutocompleteProps) {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPredictions = useCallback(async (input: string) => {
    if (input.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await placesAutocomplete({
        input,
        types: 'address',
        language,
        components: 'country:ca|country:us',
      });
      if (result.predictions?.length > 0) {
        setPredictions(result.predictions);
        setShowDropdown(true);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error('Address autocomplete error:', err);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(val), 300);
  }, [onChange, fetchPredictions]);

  const handleSelect = useCallback(async (prediction: Prediction) => {
    setIsLoading(true);
    setShowDropdown(false);

    try {
      const data = await placesDetails({ placeId: prediction.place_id, language });
      const formatted = data.result?.formatted_address || prediction.description;
      onChange(formatted);
    } catch {
      onChange(prediction.description);
    } finally {
      setIsLoading(false);
    }
  }, [onChange, language]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!showDropdown || predictions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, predictions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) handleSelect(predictions[selectedIndex]);
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  }, [showDropdown, predictions, selectedIndex, handleSelect]);

  const handleClear = useCallback(() => {
    onChange('');
    setPredictions([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  }, [onChange]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => predictions.length > 0 && setShowDropdown(true)}
        placeholder={placeholder || (language === 'fr' ? 'Rechercher une adresse...' : 'Search address...')}
        className="pl-9 pr-9"
        autoComplete="off"
      />

      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
        ) : value ? (
          <button type="button" onClick={handleClear} className="p-0.5 rounded-full hover:bg-muted" tabIndex={-1}>
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        ) : null}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div ref={dropdownRef} className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {predictions.map((p, i) => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => handleSelect(p)}
              className={`w-full px-3 py-2.5 text-left hover:bg-accent transition-colors border-b border-border last:border-b-0 ${
                i === selectedIndex ? 'bg-accent' : ''
              }`}
            >
              <div className="text-sm font-medium text-foreground">
                {p.structured_formatting?.main_text || p.description.split(',')[0]}
              </div>
              <div className="text-xs text-muted-foreground">
                {p.structured_formatting?.secondary_text || p.description.split(',').slice(1).join(',').trim()}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
